import {gql} from '@apollo/client';
import {Accordion, Box, Stack, Table, Text, createStyles} from '@mantine/core';
import {DocumentNode} from 'graphql';
import React from 'react';

import {BaseSeedDataConfig, SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {assert} from 'Asserts';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import OOTBChartingCarousel from 'studies/typed/shared/OOTBChartingCarousel';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ChipSize} from 'ui-library/chips/Chip';
import StudyChip from 'ui-library/chips/StudyChip';
import {CenteredSpinner} from 'ui-library/Spinner';
import * as DateUtil from 'util/DateUtil';

import {
  CrossStudyAnalysisSummaryQuery,
  CrossStudyAnalysisSummaryQueryVariables,
  CrossStudyAnalysisSummaryQuery_studies_StudyNode as StudySummaryStudyNode,
} from './__generated/CrossStudyAnalysisSummaryQuery';

interface MultipleStudiesData {
  studyIds: Array<string>;
}

export type MultipleStudiesSeedDataConfig = BaseSeedDataConfig<
  SeedDataConfigType.MULTIPLE_STUDIES,
  MultipleStudiesData
>;

export function validateMultipleStudiesSeedDataConfigValid(_config: MultipleStudiesSeedDataConfig): boolean {
  // None of the fields in MultipleStudiesSeedDataConfig are editable in the seed data editor,
  // so the config is always valid at this stage.
  return true;
}

export const OOTB_CROSS_STUDY_DESCRIPTION = "Preview what we'll build to help you learn about your studies.";

export const FAILED_TO_LOAD_STUDIES_MESSAGE = 'Failed to load study data. Refresh the page and try again.';

export const CROSS_STUDY_ANALYSIS_SUMMARY_QUERY: DocumentNode = gql`
  query CrossStudyAnalysisSummaryQuery($ids: [StrictString!]!) @skipAudit @engComponent(name: STUDIES) {
    studies(ids: $ids) {
      ... on StudyNode {
        id
        name
        humanId
        modifiedAt
        description
        studySchema {
          id
          studyType
        }
      }
      ...StudyChipOrInaccessibleGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('StudyChipOrInaccessible').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function StudySummaryTable({studies}: {studies: Array<StudySummaryStudyNode>}): JSX.Element {
  const {classes, cx} = useStyles();
  return (
    <Box className={classes.tableWrapper}>
      <Table horizontalSpacing="sm" verticalSpacing="sm" fontSize="sm" withColumnBorders={true}>
        <thead>
          <tr>
            <th className={cx(classes.headerCell, classes.studyCol)}>Study</th>
            <th className={cx(classes.headerCell, classes.lastModifiedCol)}>Last modified</th>
            <th className={classes.headerCell}>Description</th>
          </tr>
        </thead>
        <tbody>
          {studies.map((study) => (
            <tr key={study.id}>
              <td className={cx(classes.bodyCell, classes.studyCol)}>
                <StudyChip id={study.id} size={ChipSize.SMALL} />
              </td>
              <td className={cx(classes.bodyCell, classes.lastModifiedCol)}>
                {DateUtil.readableTime(study.modifiedAt, {showTime: false})}
              </td>
              <td className={classes.bodyCell}>{study.description}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Box>
  );
}

function CollapsibleCarousel({description}: {description: string}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Accordion
      variant="contained"
      classNames={{content: classes.carouselPanelContent, label: classes.accordionLabel}}
    >
      <Accordion.Item value="preview">
        <Accordion.Control>{description}</Accordion.Control>
        <Accordion.Panel>
          <OOTBChartingCarousel description="" />
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

export function MultipleStudiesSeedDataConfigEditor(props: {
  value: MultipleStudiesSeedDataConfig;
  onChange: (value: MultipleStudiesSeedDataConfig) => void;
}): JSX.Element | null {
  return <MultiStudyAnalysisDetails studyIds={props.value.data.studyIds} />;
}

export function MultiStudyAnalysisDetails({studyIds}: {studyIds: Array<string>}): JSX.Element | null {
  const {classes} = useStyles();
  const result = useSkippableBenchlingQuery<
    CrossStudyAnalysisSummaryQuery,
    CrossStudyAnalysisSummaryQueryVariables
  >(CROSS_STUDY_ANALYSIS_SUMMARY_QUERY, {
    skip: studyIds.length === 0,
    variables: {ids: studyIds},
    fetchPolicy: 'cache-and-network',
  });

  if (result.type === QueryResultType.SKIPPED) {
    // skip is set only when studyIds is empty; reaching here with no IDs is not an expected state.
    assert(studyIds.length > 0, 'Expected at least one study ID to be selected');
    return null;
  }

  if (result.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }

  if (result.type === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={FAILED_TO_LOAD_STUDIES_MESSAGE} />;
  }
  const accessibleStudies = accessibleObjs(result.data.studies);
  const studyType = accessibleStudies[0]?.studySchema.studyType ?? null;
  const count = studyIds.length;

  return (
    <Stack spacing={0}>
      <Stack spacing="xs" mb="md">
        <Text
          size="md"
          fw={600}
          className={classes.headingText}
        >{`You've selected ${count} ${count === 1 ? 'study' : 'studies'} to analyze.`}</Text>
        <Text size="sm" className={classes.bodyText}>
          Here's a summary of the studies you've selected
        </Text>
      </Stack>
      <StudySummaryTable studies={accessibleStudies} />
      {studyType === StudyType.PROCESS_DEVELOPMENT && (
        <Box mt={24}>
          <CollapsibleCarousel description={OOTB_CROSS_STUDY_DESCRIPTION} />
        </Box>
      )}
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  tableWrapper: {
    maxHeight: 240,
    overflowY: 'auto',
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
  },
  bodyCell: {
    lineHeight: '14px',
    verticalAlign: 'middle',
  },
  headerCell: {
    backgroundColor: theme.colors.gray[0],
    fontWeight: 500,
    lineHeight: '14px',
    verticalAlign: 'middle',
  },
  studyCol: {
    width: '25%',
    backgroundColor: theme.colors.gray[0],
  },
  lastModifiedCol: {
    width: '15%',
  },
  headingText: {
    lineHeight: '16px',
  },
  bodyText: {
    lineHeight: '14px',
  },
  accordionLabel: {
    fontWeight: 600,
    fontSize: theme.fontSizes.md,
    lineHeight: 1,
  },
  carouselPanelContent: {
    padding: 0,
  },
}));
